import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from '@mui/material';
import {
  closeBrowserNotificationPermissionDialog,
  neverAskForBrowserNotifications,
  requestBrowserNotificationPermission,
  useBrowserNotificationPermission,
  usePermissionReason,
  usePermissionRequestStatus,
} from '../../state/notifications/browser-notification-permission';
import { BrowserNotificationTest } from './BrowserNotificationTest';

export const BrowserNotificationPermissionDialog = () => {
  const reason = usePermissionReason();
  const permission = useBrowserNotificationPermission();
  const requestStatus = usePermissionRequestStatus();
  const asking = permission === 'default' && requestStatus === 'idle';
  const title =
    permission === 'granted'
      ? 'Browser notifications enabled'
      : permission === 'denied'
        ? 'Browser notifications blocked'
        : permission === 'unsupported'
          ? 'Browser notifications unavailable'
          : asking
            ? 'Enable browser notifications?'
            : 'Allow notifications in your browser';

  return (
    <Dialog
      open={reason !== undefined}
      fullWidth
      maxWidth="sm"
      onClose={closeBrowserNotificationPermissionDialog}
      slotProps={{ container: { sx: { alignItems: 'flex-start' } } }}
    >
      <DialogTitle aria-live="polite" aria-atomic="true">
        <Stack component="span" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {permission === 'granted' && <CheckCircleOutlinedIcon color="success" />}
          <span>{title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {permission === 'granted' ? (
          <Stack spacing={2}>
            <DialogContentText>
              To receive notifications of long running tasks, keep the tab open in the background. Your browser will notify you on completion.
            </DialogContentText>
            <BrowserNotificationTest />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {permission === 'denied' ? (
              <Alert severity="info">Notifications are blocked for this site. Allow them in your browser&apos;s site settings, then return here.</Alert>
            ) : permission === 'unsupported' ? (
              <Alert severity="info">Browser notifications are unavailable here. Use a supported browser over HTTPS.</Alert>
            ) : (
              <>
                {asking && reason && <DialogContentText>{reason}</DialogContentText>}
                <DialogContentText>
                  {asking
                    ? 'To enable, choose Yes, then allow notifications in your browser.'
                    : "Choose Allow in your browser's notification request. If no request appears, check this site's notification permissions in your browser."}
                </DialogContentText>
                {requestStatus === 'unanswered' && (
                  <Alert severity="info" role="status">
                    Browser notifications aren&apos;t enabled. You can try again from Settings.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {asking ? (
          <>
            <Button onClick={neverAskForBrowserNotifications}>Never ask again</Button>
            <Button onClick={closeBrowserNotificationPermissionDialog}>Not today</Button>
            <Button variant="contained" onClick={requestBrowserNotificationPermission}>
              Yes
            </Button>
          </>
        ) : (
          <Button key="close" autoFocus onClick={closeBrowserNotificationPermissionDialog}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
